import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import { Badge, Card, EmptyState, Spinner, Tabs } from '../../components/ui'
import { api } from '../../lib/api'
import { date, datetime, num } from '../../lib/format'
import type { CallLog, UsageSummary } from '../../lib/types'

type Range = '7' | '30' | '90'

export function Usage() {
  const [range, setRange] = useState<Range>('30')

  const { data, isLoading } = useQuery({
    queryKey: ['usage', range],
    queryFn: () => api.get<UsageSummary>(`/usage?days=${range}`),
  })

  const { data: logs } = useQuery({
    queryKey: ['usage-logs'],
    queryFn: () => api.get<{ items: CallLog[] }>('/usage/logs?limit=100'),
    refetchInterval: 30_000,
  })

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Використання</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Тарифікується один згенерований QR. Валідація та розбір — безкоштовні.
          </p>
        </div>
        <Tabs<Range>
          value={range}
          onChange={setRange}
          tabs={[
            { value: '7', label: '7 днів' },
            { value: '30', label: '30 днів' },
            { value: '90', label: '90 днів' },
          ]}
        />
      </header>

      {isLoading || !data ? (
        <div className="grid place-items-center py-24">
          <Spinner className="h-8 w-8 text-brand-600" />
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <Metric label="Запитів" value={num(data.totals.requests)} />
            <Metric label="Згенеровано QR" value={num(data.totals.units)} />
            <Metric
              label="Помилок"
              value={num(data.totals.errors)}
              tone={data.totals.errors > 0 ? 'warning' : undefined}
            />
          </div>

          <Card title={`Динаміка з ${date(data.since)}`}>
            {data.series.length === 0 ? (
              <EmptyState title="Даних поки немає" />
            ) : (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.series} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      className="stroke-slate-200 dark:stroke-slate-800"
                    />
                    <XAxis
                      dataKey="day"
                      tickFormatter={(v: string) => v.slice(5)}
                      tick={{ fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip
                      cursor={{ fill: 'rgba(148,163,184,.12)' }}
                      contentStyle={{ borderRadius: 12, fontSize: 13 }}
                      labelFormatter={(v: string) => date(v)}
                      formatter={(value: number, name) => [
                        num(value),
                        name === 'units' ? 'QR-кодів' : 'помилок',
                      ]}
                    />
                    <Bar dataKey="units" radius={[4, 4, 0, 0]}>
                      {data.series.map((point) => (
                        <Cell key={point.day} fill={point.errors > 0 ? '#f59e0b' : '#14b8a6'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>

          {data.byEndpoint.length > 0 && (
            <Card title="Розподіл за ендпоінтами">
              <div className="space-y-3">
                {data.byEndpoint.map((row) => {
                  const max = Math.max(...data.byEndpoint.map((r) => r.units), 1)
                  return (
                    <div key={row.endpoint}>
                      <div className="mb-1 flex justify-between text-sm">
                        <span className="font-mono text-xs">{row.endpoint}</span>
                        <span className="tabular-nums text-slate-500">{num(row.units)}</span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                        <div
                          className="h-full rounded-full bg-brand-500"
                          style={{ width: `${(row.units / max) * 100}%` }}
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
            </Card>
          )}
        </>
      )}

      <Card
        title="Журнал викликів"
        description="Останні 100 запитів. Оновлюється кожні 30 секунд."
        padded={false}
      >
        {!logs?.items.length ? (
          <EmptyState title="Журнал порожній" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-900/60 dark:text-slate-400">
                <tr>
                  <th className="px-5 py-2.5 font-medium">Час</th>
                  <th className="px-5 py-2.5 font-medium">Ендпоінт</th>
                  <th className="px-5 py-2.5 font-medium">Версія</th>
                  <th className="px-5 py-2.5 font-medium">Формат</th>
                  <th className="px-5 py-2.5 font-medium">Статус</th>
                  <th className="px-5 py-2.5 text-right font-medium">К-сть</th>
                  <th className="px-5 py-2.5 text-right font-medium">Затримка</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {logs.items.map((call, i) => (
                  <tr key={`${call.requestId}-${i}`}>
                    <td className="whitespace-nowrap px-5 py-2.5 text-xs text-slate-500 dark:text-slate-400">
                      {datetime(call.at)}
                    </td>
                    <td className="px-5 py-2.5 font-mono text-xs">{call.endpoint}</td>
                    <td className="px-5 py-2.5 text-xs">{call.version ?? '—'}</td>
                    <td className="px-5 py-2.5 text-xs">{call.format ?? '—'}</td>
                    <td className="px-5 py-2.5">
                      <Badge
                        tone={call.status < 400 ? 'success' : call.status < 500 ? 'warning' : 'danger'}
                      >
                        {call.status}
                      </Badge>
                      {call.errorCode && (
                        <span className="ml-2 font-mono text-xs text-slate-400">
                          {call.errorCode}
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-2.5 text-right tabular-nums">{call.quantity}</td>
                    <td className="px-5 py-2.5 text-right tabular-nums text-slate-500">
                      {call.latencyMs != null ? `${call.latencyMs} мс` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}

function Metric({
  label,
  value,
  tone,
}: {
  label: string
  value: string
  tone?: 'warning'
}) {
  return (
    <div className="card p-5">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </p>
      <p
        className={
          tone === 'warning'
            ? 'mt-2 text-2xl font-bold tabular-nums text-amber-600 dark:text-amber-400'
            : 'mt-2 text-2xl font-bold tabular-nums'
        }
      >
        {value}
      </p>
    </div>
  )
}
